import {
  WSTable,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import {
  PayeeRequirement,
  PayerEngagement,
  RequirementStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { selectorIsPayerEngagementActive } from "../../selectors/selectorIsPayerEngagementActive";
import { selectorIsPayerEngagementEligible } from "../../selectors/selectorIsPayerEngagementEligible";
import { selectorPayerEngagementRequirements } from "../../selectors/selectorPayerEngagementRequirements";

export interface EngagementsTableProps {
  engagements: PayerEngagement[];
  /**
   * The client's requirement instances. A v3 `PayerEngagement` carries neither
   * its requirements nor an eligibility flag, so the rollup this table shows is
   * derived per row from this list.
   */
  requirements: PayeeRequirement[];
}

export const EngagementsTable: React.FC<EngagementsTableProps> = ({
  engagements = [],
  requirements = []
}) => {
  const history = useHistory();
  const match = useRouteMatch();

  const tableData = useMemo(
    () =>
      engagements.map(engagement => ({
        id: engagement.id as string,
        data: engagement
      })),
    [engagements]
  );

  const columns: Array<WSTableColumn<PayerEngagement>> = [
    {
      config: {
        header: {
          text: "Name"
        }
      },
      renderFunction: rowItem => (
        <WSTableCell text={rowItem.data.engagementName ?? undefined} />
      )
    },
    {
      config: {
        header: {
          text: "Requirements"
        }
      },
      renderFunction: rowItem => {
        const engagement = rowItem.data;

        if (!selectorIsPayerEngagementActive(engagement)) {
          return (
            <WSTableCell
              pill={{
                theme: "neutral",
                text: "Archived",
                icon: "archive"
              }}
            />
          );
        }

        const engagementRequirements = selectorPayerEngagementRequirements({
          engagement,
          requirements
        });
        const allLength = engagementRequirements.length;
        const completedLength = engagementRequirements.filter(
          requirement => requirement.status === RequirementStatus.COMPLETED
        ).length;
        const isComplete = completedLength === allLength && allLength > 0;

        const isEligible = selectorIsPayerEngagementEligible({
          engagement,
          requirements
        });
        const secondaryText = isEligible
          ? "Eligible for payments"
          : "Not eligible for payments";
        const secondaryTextColor = isEligible ? undefined : "amber400";

        if (allLength === 0) {
          return (
            <WSTableCell
              pill={{
                theme: "neutral",
                text: "None required",
                icon: "info-circle"
              }}
              secondaryText={secondaryText}
              secondaryTextColor={secondaryTextColor}
            />
          );
        }

        return (
          <WSTableCell
            secondaryText={secondaryText}
            secondaryTextColor={secondaryTextColor}
            pill={{
              theme: isComplete ? "success" : "warning",
              text: `${completedLength} of ${allLength} Complete`,
              icon: true
            }}
          />
        );
      }
    }
  ];

  return (
    <WSTable
      columns={columns}
      tableData={tableData}
      onRowClick={item => {
        history.push(`${match.url}/engagements/${item.id}`, {
          backPath: match.url
        });
      }}
      onRowControlClick={item => {
        openInNewTab(`${match.url}/engagements/${item.id}`);
      }}
      onRowMousewheelClick={item => {
        openInNewTab(`${match.url}/engagements/${item.id}`);
      }}
      data-testid="engagementsTable"
    />
  );
};
